/**
 * Helpers for running inside a host product's iframe.
 *
 * The BridgeU visits screens are embedded in BridgeU (an iframe on
 * /advisor/visits). Most of the app is indifferent to that, but any flow that
 * leaves the app for a third-party consent screen is not: Google and Zoom both
 * serve `X-Frame-Options: DENY` on their OAuth pages, so navigating the frame
 * to one replaces the app with a "content is blocked" error and the counselor
 * has no way back.
 *
 * Deliberately not an origin allowlist. Which host is framing us is the right
 * question for a postMessage targetOrigin, but the wrong one here: the only
 * signal for the host's identity is document.referrer, and a host sending
 * `Referrer-Policy: no-referrer` would make us believe we are top-level and
 * navigate the frame into exactly the blocked page this avoids. Whether we are
 * framed at all is what matters, and that is knowable regardless of referrer.
 */

/**
 * True when the app is not the top-level document. Reading window.parent across
 * origins can throw in some browsers; a throw means there is a cross-origin
 * parent, which is itself the answer.
 */
export const isFramed = () => {
  try {
    return window.parent !== window;
  } catch (e) {
    return true;
  }
};

/**
 * Send the browser to a URL that refuses to be framed -- an OAuth consent
 * screen, typically. Framed, that has to be a new tab, so the host page and the
 * app inside it both survive the round trip. Standalone it stays an ordinary
 * same-tab navigation, which is what every caller did before.
 *
 * Returns the window target used, so callers can adapt their copy.
 */
export const openOutsideFrame = url => {
  if (!url) return null;

  const target = isFramed() ? "_blank" : "_self";
  window.open(url, target, isFramed() ? "noopener" : "");
  return target;
};

/** The message a host listens for to open an Explore screen for the user. */
export const OPEN_SSO = "explore:open-sso";

/**
 * The `redirect_url` values the SSO endpoint understands, as the host will pass
 * them straight through. `settings` resolves to `settings#zoom` server-side.
 */
export const SSO_SCHOOL_DETAILS = "school-details";
export const SSO_SETTINGS = "settings";

/**
 * Ask the host product to open one of its Explore SSO links.
 *
 * Framed, this app cannot open Explore for itself: browsers partition the
 * embedded frame's storage, so a new same-origin tab carries no session and
 * lands on sign-in. Only the host holds the key that mints one — BridgeU posts
 * the user's `cialfo_sso_key` to `/api/v3/bridgeu/sessions/sso` and opens the
 * URL that comes back, which is what its own School Settings button does.
 *
 * So the frame asks, and the host mints. `redirectUrl` is the value that
 * endpoint understands — "settings" or "school-details".
 *
 * Returns false when there is no host to ask, so callers can fall back to
 * opening Explore directly, which is correct standalone: there the session is
 * this document's own.
 *
 * The message carries no credential — the token is minted host-side and never
 * passes through here — so `document.referrer` is a good enough target: worst
 * case it is empty and we do not post at all. The host is expected to check
 * `event.origin` before acting on it.
 */
export const requestHostSso = redirectUrl => {
  if (!redirectUrl || !isFramed()) return false;

  const hostOrigin = document.referrer
    ? new URL(document.referrer).origin
    : null;

  if (!hostOrigin) return false;

  window.parent.postMessage(
    { type: OPEN_SSO, redirect_url: redirectUrl },
    hostOrigin,
  );

  return true;
};
